/**
 * Blog posts API client
 *
 * @changeHistory
 * - 2026-05-12: Initial creation - Added blog posts API client for AP-70 (AI Agent)
 * - 2026-05-17: Added storage-only image upload for inline blog images (Claude)
 * - 2026-05-18: Added optional PDA author identifiers to the client blog contract (Codex)
 * - 2026-07-07: Added shared blog-post query options for breadcrumb labels (Codex)
 * - 2026-09-30: Added explicit blog cover removal to the update contract (Copilot)
 */

import {
  queryOptions,
  type DataTag,
  type UndefinedInitialDataOptions,
} from '@tanstack/react-query';
import { useAuthStore } from '../shared/stores/auth.store';
import { API_BASE_URL, apiClient } from './api-client';

export type BlogPostStatus = 'draft' | 'published';
export type BlogPostFilterStatus = BlogPostStatus | 'all';

export type BlogContentBlock =
  | { type: 'paragraph'; text: string }
  | { type: 'heading'; text: string }
  | { type: 'list'; items: string[] }
  | { type: 'image'; url: string; caption?: string }
  | { type: 'quote'; text: string; author: string };

export interface BlogPostAuthor {
  id?: string;
  authorId?: string;
  name: string;
  title: string;
  bio: string;
  image: string;
  specialties: string[];
}

export interface BlogPostRecord {
  id: string;
  title: string;
  slug: string;
  excerpt?: string;
  content: BlogContentBlock[];
  coverImageUrl?: string;
  tags?: string[];
  author: BlogPostAuthor;
  status: BlogPostStatus;
  publishedAt?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

type BlogPostQueryKey = readonly ['blog-post', string];
type BlogPostQueryOptions = UndefinedInitialDataOptions<
  BlogPostRecord,
  Error,
  BlogPostRecord,
  BlogPostQueryKey
> & {
  queryKey: DataTag<BlogPostQueryKey, BlogPostRecord, Error>;
};

export interface BlogPostListResult {
  items: BlogPostRecord[];
}

export interface BlogPostListParams {
  status?: BlogPostFilterStatus;
}

export interface CreateBlogPostPayload {
  title: string;
  slug: string;
  excerpt?: string;
  content: BlogContentBlock[];
  coverImageUrl?: string;
  tags?: string[];
  authorId: string;
}

export interface UpdateBlogPostPayload {
  title?: string;
  slug?: string;
  excerpt?: string;
  content?: BlogContentBlock[];
  coverImageUrl?: string | null;
  tags?: string[];
  authorId?: string;
}

type ListResponse = {
  status: 'success';
  data: BlogPostListResult;
};

type SingleResponse = {
  status: 'success';
  data: BlogPostRecord;
};

export const blogPostsApi = {
  async list(params: BlogPostListParams = {}): Promise<BlogPostListResult> {
    const query = new URLSearchParams();

    if (params.status) {
      query.set('status', params.status);
    }

    const queryString = query.toString();
    const endpoint = `/blog-posts${queryString ? `?${queryString}` : ''}`;
    const response = await apiClient.get<ListResponse>(endpoint);

    return response.data;
  },

  async getBySlug(slug: string): Promise<BlogPostRecord> {
    const response = await apiClient.get<SingleResponse>(
      `/blog-posts/slug/${encodeURIComponent(slug)}`
    );

    return response.data;
  },

  async create(payload: CreateBlogPostPayload): Promise<BlogPostRecord> {
    const response = await apiClient.post<SingleResponse>('/blog-posts', payload);

    return response.data;
  },

  async update(id: string, payload: UpdateBlogPostPayload): Promise<BlogPostRecord> {
    const response = await apiClient.put<SingleResponse>(`/blog-posts/${id}`, payload);

    return response.data;
  },

  async publish(id: string): Promise<BlogPostRecord> {
    const response = await apiClient.patch<SingleResponse>(`/blog-posts/${id}/publish`);

    return response.data;
  },

  async unpublish(id: string): Promise<BlogPostRecord> {
    const response = await apiClient.patch<SingleResponse>(`/blog-posts/${id}/unpublish`);

    return response.data;
  },

  async delete(id: string): Promise<void> {
    await apiClient.delete<void>(`/blog-posts/${id}`);
  },

  /**
   * Upload one or more image files to the blog-post CDN folder and get
   * back the public URLs. Used by the rich-text editor toolbar / drop /
   * paste handlers to insert <img> tags inline in the body.
   */
  async uploadImagesToStorage(files: File[]): Promise<string[]> {
    if (files.length === 0) {
      return [];
    }

    const formData = new FormData();
    files.forEach((file) => {
      formData.append('images', file);
    });

    const { accessToken } = useAuthStore.getState();
    const response = await fetch(`${API_BASE_URL}/blog-posts/storage/images`, {
      method: 'POST',
      body: formData,
      credentials: 'include',
      headers: accessToken
        ? {
            Authorization: `Bearer ${accessToken}`,
          }
        : undefined,
    });

    if (!response.ok) {
      let message = 'Failed to upload image';

      try {
        const error = (await response.json()) as { message?: string };
        if (typeof error?.message === 'string' && error.message.length > 0) {
          message = error.message;
        }
      } catch {
        // Ignore non-JSON error bodies and keep fallback message.
      }

      throw new Error(message);
    }

    const result = (await response.json()) as { data: { urls: string[] } };
    return result.data.urls;
  },
};

export const getBlogPostBySlugQueryOptions = (slug: string): BlogPostQueryOptions =>
  queryOptions<BlogPostRecord, Error, BlogPostRecord, BlogPostQueryKey>({
    queryKey: ['blog-post', slug],
    queryFn: async () => blogPostsApi.getBySlug(slug),
  });
